import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Truck, RotateCcw, ShieldCheck, Headphones } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

const formatPrice = (value) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
    .format(Number(value) || 0);

/**
 * Thin promise strip above the header.
 *
 * The free-shipping threshold is read from store settings rather than hardcoded, so
 * the promise here can never contradict what the cart actually charges.
 */
export function AnnouncementBar() {
  const { freeThreshold } = useSettings();
  const [index, setIndex] = useState(0);

  const messages = [
    { icon: Truck, text: `Free delivery on orders above ${formatPrice(freeThreshold)}` },
    { icon: RotateCcw, text: '7-day easy returns on every order' },
    { icon: ShieldCheck, text: '100% authentic products, secure payments' },
    { icon: Headphones, text: 'Need help? Talk to our team' }
  ];

  // Rotate on mobile, where there's only room for one line.
  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % messages.length), 4000);
    return () => clearInterval(timer);
  }, [messages.length]);

  const Current = messages[index].icon;

  return (
    /*
      Blue gradient with a hard orange edge on the right.
      A blue→orange blend was tried first and looked muddy: they are near-complementary,
      so interpolating between them passes through desaturated brown. Keeping each
      colour solid and butting them together keeps both hues clean.
    */
    <div
      className="relative bg-gradient-to-r from-brand-blue-dark to-brand-blue text-white"
      data-testid="announcement-bar"
    >
      <div className="absolute inset-y-0 right-0 w-1.5 bg-brand-orange" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Desktop: all promises at once — a customer shouldn't have to wait for a rotation */}
        <div className="hidden md:flex items-center justify-between h-9 text-[11px] font-body">
          <div className="flex items-center gap-6">
            {messages.slice(0, 3).map((m, i) => (
              <span key={i} className="flex items-center gap-1.5 text-white/85">
                <m.icon className="w-3.5 h-3.5 text-white/60" />
                {m.text}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <Link to="/track-order" className="text-white/85 hover:text-white transition-colors">
              Track Order
            </Link>
            <span className="text-white/20">|</span>
            <Link to="/contact" className="text-white/85 hover:text-white transition-colors">
              Help
            </Link>
          </div>
        </div>

        {/* Mobile: one rotating line */}
        <div className="md:hidden flex items-center justify-center gap-2 h-9 text-[11px] font-body text-white/85">
          <Current className="w-3.5 h-3.5 text-white/60 flex-shrink-0" />
          <span key={index} className="animate-in fade-in duration-500 truncate">
            {messages[index].text}
          </span>
        </div>
      </div>
    </div>
  );
}
